/**
 * 🔐 权限Hook | Permission Hook
 * 
 * 检查用户是否有权限执行特定操作
 */

import { useAuth, Role } from './useAuth';

/**
 * 权限配置（与后端保持一致）
 */
export const PERMISSIONS: Record<string, Record<string, Role[]>> = {
  'post-release': {
    'start_test': ['admin', 'qa'],
    'stop_test': ['admin', 'qa'],
    'view_report': ['admin', 'qa', 'dev', 'reviewer'],
    'download_logs': ['admin', 'qa', 'dev', 'reviewer'],
    'delete_run': ['admin', 'qa'],
    'select_devices': ['admin', 'qa'],
    'select_integrations': ['admin', 'qa'],
  },
  
  'test-runner': {
    'start_test': ['admin', 'qa'],
    'stop_test': ['admin', 'qa'],
    'view_history': ['admin', 'qa', 'dev', 'reviewer'],
    'cleanup': ['admin', 'qa'],
  },
  
  'device-monitor': {
    'start_monitoring': ['admin', 'qa'],
    'stop_monitoring': ['admin', 'qa'],
    'view_screenshot': ['admin', 'qa', 'dev', 'reviewer'],
    'control_device': ['admin', 'qa'],
    'download_screenshot': ['admin', 'qa', 'dev', 'reviewer'],
  },
  
  'apk-management': {
    'upload_apk': ['admin', 'qa'],
    'download_apk': ['admin', 'qa', 'dev', 'reviewer'],
    'activate_apk': ['admin', 'qa'],
    'view_list': ['admin', 'qa', 'dev', 'reviewer'],
    'delete_apk': ['admin', 'qa'],
  },
  
  'sql-validation': {
    'trigger_validation': ['admin', 'qa'],
    'view_report': ['admin', 'qa', 'dev', 'reviewer'],
    'download_report': ['admin', 'qa', 'dev', 'reviewer'],
  },
  
  'configuration': {
    'view_config': ['admin', 'qa', 'reviewer'],
    'edit_config': ['admin', 'qa'],
  },
  
  'users': {
    'manage_users': ['admin'],
    'view_users': ['admin'],
  },
};

/**
 * 🎯 usePermission Hook
 * 
 * @returns { hasPermission, role, isAdmin, isQA, isDev, isReviewer }
 */
export function usePermission() {
  const { role } = useAuth();
  
  /**
   * 检查是否有权限（支持两种调用方式）
   * 1. hasPermission('post-release', 'start_test')
   * 2. hasPermission('START_TEST') - 简化版，直接使用权限名称
   */
  const hasPermission = (resourceOrPermission: string, action?: string): boolean => {
    if (!role) return false;
    
    // Admin有所有权限
    if (role === 'admin') return true;
    
    // 如果只传了一个参数，使用简化的权限名称映射
    if (!action) {
      const SIMPLE_PERMISSIONS: Record<string, Role[]> = {
        'VIEW_DASHBOARD': ['admin', 'qa', 'dev', 'reviewer'],
        'VIEW_REPORTS': ['admin', 'qa', 'dev', 'reviewer'],
        'DOWNLOAD_LOGS': ['admin', 'qa', 'dev', 'reviewer'],
        'START_TEST': ['admin', 'qa'],
        'STOP_TEST': ['admin', 'qa'],
        'UPLOAD_APK': ['admin', 'qa'],
        'MANAGE_CONFIG': ['admin', 'qa'],
        'MANAGE_USERS': ['admin'],
      };
      
      const allowedRoles = SIMPLE_PERMISSIONS[resourceOrPermission];
      if (!allowedRoles) return false;
      return allowedRoles.includes(role);
    }
    
    // 否则使用原有的 resource.action 方式
    const resourcePermissions = PERMISSIONS[resourceOrPermission];
    if (!resourcePermissions) return false;
    
    const allowedRoles = resourcePermissions[action];
    if (!allowedRoles) return false;
    
    return allowedRoles.includes(role);
  };
  
  /**
   * 检查是否有特定角色
   */
  const hasRole = (requiredRole: Role): boolean => {
    return role === requiredRole;
  };
  
  /**
   * 检查是否有任一角色
   */
  const hasAnyRole = (requiredRoles: Role[]): boolean => {
    if (!role) return false;
    return requiredRoles.includes(role);
  };
  
  return {
    hasPermission,
    hasRole,
    hasAnyRole,
    role,
    isAdmin: role === 'admin',
    isQA: role === 'qa',
    isDev: role === 'dev',
    isReviewer: role === 'reviewer',
  };
}

/**
 * 🎯 权限常量（用于UI显示）
 */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin | 管理员',
  qa: 'QA | 测试',
  dev: 'Dev | 开发',
  reviewer: 'Reviewer | 审核',
};

export const ROLE_COLORS: Record<Role, 'error' | 'success' | 'info' | 'warning'> = {
  admin: 'error',    // 红色
  qa: 'success',     // 绿色
  dev: 'info',       // 蓝色
  reviewer: 'warning', // 橙色
};

